import type { SerializedEditorState } from 'lexical';

import type { ExportOptions, WorldRect } from '@/canvas-export/export-types';

import { DEFAULT_MARGINS } from './paper';
import type { PatternLanguage } from './pattern-language';
import type { PageSetup, PaperSize } from './paper';

export type { Margins, PageSetup, PaperSize } from './paper';

export const MIN_ELEMENT_WIDTH = 80;
export const MIN_ELEMENT_HEIGHT = 40;
export const DRAG_THRESHOLD = 4;
/** The most characters a flowing text may hold, and the largest |offset| (px) a split or continuation override may carry. */
export const MAX_FLOW_CHARS = 1_000_000;
export const MAX_FLOW_OFFSET = 10_000;

/** A paragraph split mid-way: the break lies `offset` characters into break unit `unit`. */
export type FlowBreakOffset = { unit: number; offset: number };
/** A continuation box moved (`dx`, `dy`) and/or resized (`width`) from its default spot. */
export type FlowOverride = { dx?: number; dy?: number; width?: number };

export type Point = { x: number; y: number };
export type Geometry = { x: number; y: number; width: number; height: number };

/** The diagram a generated element (legend, generated text) was made from. */
export type GeneratedSourceRef = { diagramId: string; kind: 'crochet' | 'knitting' };

export type TextData = {
    type: 'text';
    /** Present when the text was generated from a diagram; `hash` fingerprints what it was generated from. */
    generated?: { source: GeneratedSourceRef; hash: string };
    editorState: SerializedEditorState | null;
    /**
     * The index of the first unit on each later page, so `flowBreaks.length + 1` is the number of pages the
     * text reaches. The values are non-decreasing integers >= 1. A repeated value marks a page covered by a
     * too-tall unit; that page's slice is empty. The key is absent when the text fits on one page.
     * Written by the layout (`applyTextFlow`), never by the designer.
     */
    flowBreaks?: number[];
    flowBreakOffsets?: Array<FlowBreakOffset | null>;
    flowOverrides?: Array<FlowOverride | null>;
};
/** The asset-bearing half shared by image and diagram elements: either a
 *  content-addressed assetId or a legacy inline data URL, never both. */
export type AssetRef = {
    naturalAspect: number;
} & (
    | { assetId: string; src?: never }
    | { assetId?: never; src: string }
);

export type ImageData = { type: 'image' } & AssetRef;

/** What a diagram embed was rendered FROM — enough to re-render it on Refresh. */
export type DiagramSource = {
    diagramId: string;
    kind: 'crochet' | 'knitting';
    worldRect: WorldRect;
    options: ExportOptions;
};

export type DiagramData = { type: 'diagram'; source: DiagramSource } & AssetRef;

export type LegendRow = { stitchId: string; abbrev: string; name: string };

export type LegendData = {
    type: 'legend';
    source: GeneratedSourceRef;
    title: string;
    rows: LegendRow[];
    hash: string;
};

/** Bounds shared by the frontend parser and the backend validator. */
export const LEGEND_LIMITS = {
    rows: 500,
    title: 200,
    abbrev: 40,
    name: 200,
    stitchId: 100,
    diagramId: 100,
    hash: 64,
} as const;

export type ElementData = TextData | ImageData | DiagramData | LegendData;

export type PatternElement = Geometry & { id: string } & ElementData;

export type TextPatternElement = Extract<PatternElement, { type: 'text' }>;
export type LegendPatternElement = Extract<PatternElement, { type: 'legend' }>;

/** Who made a page. `'auto'`: text flow created it. */
export type PageOrigin = 'auto';

export type Page = {
    id: string;
    elements: PatternElement[];
    /** Absent means the designer added the page; `'auto'` means text flow created it, and `reconcileFlowPages` may remove it. */
    origin?: PageOrigin;
};

/** True for a page text flow created. Any other `origin` (or none) reads as a page the designer added. */
export function isAutoPage(page: Page): boolean {
    return page.origin === 'auto';
}

/** `setup` (paper size and margins) is shared by every page of the pattern. */
export type PatternDocument = { setup: PageSetup; pages: Page[] };

export function newElementId(): string {
    return `el-${crypto.randomUUID()}`;
}

export function newPageId(): string {
    return `page-${crypto.randomUUID()}`;
}

export function createEmptyPage(): Page {
    return { id: newPageId(), elements: [] };
}

export function createEmptyDocument(paperSize: PaperSize = 'letter', language: PatternLanguage = 'en'): PatternDocument {
    return {
        setup: { paperSize, margins: { ...DEFAULT_MARGINS }, language },
        pages: [createEmptyPage()],
    };
}
